import React, {useState} from 'react'
import {BaseEdge, EdgeProps, getSmoothStepPath} from 'reactflow'
import {
  REACT_FLOW_MIME_TYPE,
  DROP_EFFECT,
  REQUIRED_EXTENSIONS,
  EDGE_STYLE,
  NodeType,
} from '../../../../../../Utils/Constant/FlowConstants'
import './CustomEdge.scss'
import {Modal} from 'react-bootstrap'
import NodeSelect from './NodeSelect'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'

interface CustomEdgeProps extends EdgeProps {
  addNodeBetweenEdges: (
    nodeType: NodeType,
    source: string,
    target: string,
    position: {x: number; y: number}
  ) => void
  isEdit: boolean
  payload?: any
}

const CustomEdge: React.FC<CustomEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  markerEnd,
  source,
  target,
  addNodeBetweenEdges,
  sourcePosition,
  targetPosition,
  data,
  isEdit,
  payload,
}) => {
  const {t} = useI18n()
  const [isDragOver, setIsDragOver] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [isPlusHover, setIsPlusHover] = useState(false)
  const [isLabelHover, setIsLabelHover] = useState(false)
  const [isEndPlusHover, setIsEndPlusHover] = useState(false)
  const [isEndPlusDragOver, setIsEndPlusDragOver] = useState(false)
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  })

  // Check if this is a yes/no edge
  const isYesNoEdge = data?.label === 'yes' || data?.label === 'no'

  // Calculate position for the plus icon at the end of yes/no edges
  const endPlusX = targetX - 20
  const endPlusY = targetY - 50

  // Expanded drop halo sizes for more forgiving drag-and-drop (rectangular)
  const CENTRAL_DROP_HALO_WIDTH = 220 // px
  const CENTRAL_DROP_HALO_HEIGHT = 64 // px
  const END_DROP_HALO_WIDTH = 220 // px
  const END_DROP_HALO_HEIGHT = 64 // px

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    const nodeType = event.dataTransfer.getData(REACT_FLOW_MIME_TYPE)
    if (!nodeType) return

    const position = {x: labelX, y: labelY}
    addNodeBetweenEdges(nodeType.toLowerCase() as NodeType, source, target, position)
  }

  const handleEndPlusDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsEndPlusDragOver(false)

    const nodeType = event.dataTransfer.getData(REACT_FLOW_MIME_TYPE)
    if (!nodeType) return

    const position = {x: targetX, y: targetY + 50}
    addNodeBetweenEdges(nodeType.toLowerCase() as NodeType, source, target, position)
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = DROP_EFFECT.MOVE
    setIsDragOver(true)
  }

  const handleEndPlusDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = DROP_EFFECT.MOVE
    setIsEndPlusDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
  }

  const handleEndPlusDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    setIsEndPlusDragOver(false)
  }

  const renderEdgeLabel = (label: string) => {
    return (
      <div
        className={`label-edge-${label.toLowerCase()} d-flex align-items-center justify-content-center text-white rounded-pill fw-bold px-3 py-1`}
      >
        {label}
      </div>
    )
  }

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          ...(isDragOver ? EDGE_STYLE.hover : EDGE_STYLE.normal),
        }}
      />

      {/* Large, invisible drop halo around the central plus/label to allow forgiving drops */}
      {!isYesNoEdge && (
        <foreignObject
          width={CENTRAL_DROP_HALO_WIDTH}
          height={CENTRAL_DROP_HALO_HEIGHT}
          x={labelX - CENTRAL_DROP_HALO_WIDTH / 2}
          y={labelY - CENTRAL_DROP_HALO_HEIGHT / 2}
          className={`flow-drop-halo of-visible ${isDragOver ? 'dragover' : ''}`}
          data-edge-id={id}
          data-source={source}
          data-target={target}
          data-type='central'
          data-pos-x={labelX}
          data-pos-y={labelY}
          requiredExtensions={REQUIRED_EXTENSIONS.XHTML}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className='flow-drop-indicator' />
        </foreignObject>
      )}
      {data?.label ? (
        <foreignObject
          width={40}
          height={24}
          x={labelX - 20}
          y={labelY - 12}
          className={`of-visible ${isYesNoEdge ? 'pe-none' : ''}`}
          requiredExtensions={REQUIRED_EXTENSIONS.XHTML}
          {...(!isYesNoEdge
            ? {
                onDrop: handleDrop,
                onDragOver: handleDragOver,
                onDragLeave: handleDragLeave,
                onMouseEnter: () => setIsLabelHover(true),
                onMouseLeave: () => setIsLabelHover(false),
              }
            : {})}
        >
          <div
            className={isLabelHover && !isYesNoEdge ? 'edge-button-highlight' : ''}
            tabIndex={0}
            draggable={false}
            onPointerDown={(e) => {
              if (isYesNoEdge) return
              e.preventDefault()
              setShowSidebar(true)
            }}
          >
            {renderEdgeLabel(data.label)}
          </div>
        </foreignObject>
      ) : (
        <foreignObject
          width={32}
          height={32}
          x={labelX - 16}
          y={labelY - 16}
          className='edge-button-foreignobject of-visible'
          requiredExtensions={REQUIRED_EXTENSIONS.XHTML}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onMouseEnter={() => setIsPlusHover(true)}
          onMouseLeave={() => setIsPlusHover(false)}
        >
          {isDragOver && (
            <div className='edge-drop-backdrop' />
          )}
          <div
            className={`edge-button d-flex justify-content-center align-items-center bg-white w-100 h-100 edge-plus-btn ${
              isDragOver || isPlusHover ? 'edge-button-highlight' : ''
            }`}
            style={{borderRadius: '50%'}}
            tabIndex={0}
            draggable={false}
            onPointerDown={(e) => {
              e.preventDefault()
              setShowSidebar(true)
            }}
          >
            <i className='bi bi-plus-lg edge-plus-icon'></i>
          </div>
        </foreignObject>
      )}

      {/* Plus icon at the end of yes/no edges */}
      {isYesNoEdge && (
        <>
          {/* Large, invisible drop halo near the end of yes/no edges */}
          <foreignObject
            width={END_DROP_HALO_WIDTH}
            height={END_DROP_HALO_HEIGHT}
            x={endPlusX - END_DROP_HALO_WIDTH / 2 + 16}
            y={endPlusY - END_DROP_HALO_HEIGHT / 2 + 16}
            className={`flow-drop-halo of-visible flow-drop-halo-end ${
              isEndPlusDragOver ? 'dragover' : ''
            }`}
            data-edge-id={id}
            data-source={source}
            data-target={target}
            data-type='end'
            data-pos-x={targetX}
            data-pos-y={targetY + 50}
            requiredExtensions={REQUIRED_EXTENSIONS.XHTML}
            onDrop={handleEndPlusDrop}
            onDragOver={handleEndPlusDragOver}
            onDragLeave={handleEndPlusDragLeave}
          >
            <div className='flow-drop-indicator' />
          </foreignObject>

          {/* Original small interactive plus button at the end */}
          <foreignObject
            width={32}
            height={32}
            x={endPlusX}
            y={endPlusY}
            className='edge-button-foreignobject of-visible'
            requiredExtensions={REQUIRED_EXTENSIONS.XHTML}
            onDrop={handleEndPlusDrop}
            onDragOver={handleEndPlusDragOver}
            onDragLeave={handleEndPlusDragLeave}
            onMouseEnter={() => setIsEndPlusHover(true)}
            onMouseLeave={() => setIsEndPlusHover(false)}
          >
            {isEndPlusDragOver && (
              <div className='edge-drop-backdrop' />
            )}
            <div
              className={`edge-button d-flex justify-content-center align-items-center bg-white w-100 h-100 edge-plus-btn ${
                isEndPlusDragOver || isEndPlusHover ? 'edge-button-highlight' : ''
              }`}
              style={{borderRadius: '50%'}}
              tabIndex={0}
              draggable={false}
              onPointerDown={(e) => {
                e.preventDefault()
                setShowSidebar(true)
              }}
            >
              <i className='bi bi-plus-lg edge-plus-icon'></i>
            </div>
          </foreignObject>
        </>
      )}

      <Modal
        dialogClassName='modal-dialog-centered mw-500px'
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='flex-grow-1 text-center'>{t('FLOW.NODE_SELECT')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NodeSelect
            onSelect={(nodeType) => {
              addNodeBetweenEdges(nodeType as NodeType, source, target, {
                x: labelX,
                y: labelY,
              })
              setShowSidebar(false)
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default CustomEdge
